import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface BundleUsageStats {
  bundle_id: string;
  bundle_name: string;
  usage_count: number;
  total_revenue: number;
  avg_deal_value: number;
  last_used_at: string | null;
}

export function useBundleUsageStats() {
  return useQuery({
    queryKey: ['bundle-usage-stats'],
    queryFn: async () => {
      // Get all line items that have a bundle_id
      const { data: lineItems, error: lineItemsError } = await supabase
        .from('deal_line_items')
        .select(`
          bundle_id,
          total_price,
          created_at,
          deal:deals(id, amount)
        `)
        .not('bundle_id', 'is', null);

      if (lineItemsError) throw lineItemsError;

      // Get all bundles for name mapping
      const { data: bundles, error: bundlesError } = await supabase
        .from('product_bundles')
        .select('id, name');

      if (bundlesError) throw bundlesError;

      const bundleMap = new Map(bundles?.map(b => [b.id, b.name]) || []);

      // Aggregate stats by bundle
      const statsMap = new Map<string, {
        bundle_id: string;
        bundle_name: string;
        usage_count: number;
        total_revenue: number;
        deal_values: number[];
        last_used_at: string | null;
      }>();

      const processedDeals = new Set<string>();

      lineItems?.forEach((item: any) => {
        if (!item.bundle_id) return;

        const existing = statsMap.get(item.bundle_id);
        const dealId = item.deal?.id;
        const dealAmount = item.deal?.amount || 0;

        if (existing) {
          existing.usage_count += 1;
          existing.total_revenue += item.total_price;
          if (dealId && !processedDeals.has(`${item.bundle_id}-${dealId}`)) {
            existing.deal_values.push(dealAmount);
            processedDeals.add(`${item.bundle_id}-${dealId}`);
          }
          if (!existing.last_used_at || item.created_at > existing.last_used_at) {
            existing.last_used_at = item.created_at;
          }
        } else {
          statsMap.set(item.bundle_id, {
            bundle_id: item.bundle_id,
            bundle_name: bundleMap.get(item.bundle_id) || 'Unknown Bundle',
            usage_count: 1,
            total_revenue: item.total_price,
            deal_values: dealId ? [dealAmount] : [],
            last_used_at: item.created_at,
          });
          if (dealId) {
            processedDeals.add(`${item.bundle_id}-${dealId}`);
          }
        }
      });

      // Calculate averages and format results
      const stats: BundleUsageStats[] = Array.from(statsMap.values()).map(s => ({
        bundle_id: s.bundle_id,
        bundle_name: s.bundle_name,
        usage_count: s.usage_count,
        total_revenue: s.total_revenue,
        avg_deal_value: s.deal_values.length > 0 
          ? s.deal_values.reduce((a, b) => a + b, 0) / s.deal_values.length 
          : 0,
        last_used_at: s.last_used_at,
      }));

      // Sort by usage count descending
      return stats.sort((a, b) => b.usage_count - a.usage_count);
    },
  });
}

export function useBundleDeals(bundleId: string) {
  return useQuery({
    queryKey: ['bundle-deals', bundleId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('deal_line_items')
        .select(`
          id,
          total_price,
          created_at,
          deal:deals(
            id,
            title,
            amount,
            status,
            account:accounts(name)
          )
        `)
        .eq('bundle_id', bundleId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data;
    },
    enabled: !!bundleId,
  });
}
